import React, { Fragment } from 'react';
import { Table, Header, Container } from 'semantic-ui-react';
import { EnhancedDropdown } from '../defaultClassComponents.jsx';

export default function Dashboard({
  exporterPath,
  statusCount,
  devicesTotal,
  expiredTotal,
  expiringTotal,
  faultyTotal,
  agingSummary,
}) {
  const exporterURL = (query, details = '') =>
    `${exporterPath}?query=${encodeURIComponent(query)}` +
    (details ? `&details=${details}` : '');
  const isNegativeRow = (total) => total > 0;

  const totalContent = (query, total, details = '') => {
    if (total === 0) {
      return total;
    }
    return total > 0 && <a href={exporterURL(query, details)}>{total}</a>;
  };

  const statusTotalContent = (query) => {
    return totalContent(query, statusCount[query]);
  };

  const [agingSummaryDropdown, setAgingSummaryDropdown] = React.useState(
    Object.keys(agingSummary)[0],
  );

  const agingSummaryOptions = () => {
    return Object.keys(agingSummary).map((key, idx) => {
      return {
        key: idx,
        text: key,
        value: key,
        active: agingSummaryDropdown === key,
        onClick: (_, data) => setAgingSummaryDropdown(data.value),
      };
    });
  };

  return (
    <Fragment>
      <Header>Actionable Counts:</Header>
      <Table
        definition
        striped
        className='actionable-counts'
        columns={3}
        style={{ marginBottom: '60px' }}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>Count Description</Table.HeaderCell>
            <Table.HeaderCell># Devices</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row negative={isNegativeRow(expiringTotal)}>
            <Table.Cell>
              <Container
                fluid
                style={{
                  display: 'flex',
                  placeContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                Last Inspection Aging Summary
                <EnhancedDropdown
                  button
                  className='icon'
                  icon='dropdown'
                  options={agingSummaryOptions()}
                  value={agingSummaryDropdown}
                />
              </Container>
            </Table.Cell>
            <Table.Cell>
              Devices grouped by inspection date and filtered by range.
            </Table.Cell>
            <Table.Cell className='counter'>
              {totalContent(
                'aging_summary',
                agingSummary[agingSummaryDropdown],
                agingSummaryDropdown,
              )}
            </Table.Cell>
          </Table.Row>
          <Table.Row negative={isNegativeRow(expiringTotal)}>
            <Table.Cell>Inspection Due in next 60 Days</Table.Cell>
            <Table.Cell>Inspection Due in next 60 days.</Table.Cell>
            <Table.Cell className='counter'>
              {totalContent('expiring', expiringTotal)}
            </Table.Cell>
          </Table.Row>
          <Table.Row negative={isNegativeRow(expiredTotal)}>
            <Table.Cell>Past Inspection Date</Table.Cell>
            <Table.Cell>Past Inspection Date.</Table.Cell>
            <Table.Cell className='counter'>
              {totalContent('expired', expiredTotal)}
            </Table.Cell>
          </Table.Row>
          <Table.Row negative={isNegativeRow(faultyTotal)}>
            <Table.Cell>Provisioning Defect</Table.Cell>
            <Table.Cell>Provisioning Defect.</Table.Cell>
            <Table.Cell className='counter'>
              {totalContent('faulty', faultyTotal)}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      <Header>Counts by Status:</Header>
      <Table
        definition
        striped
        className='counts-by-status'
        columns={3}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>Count Description</Table.HeaderCell>
            <Table.HeaderCell># Devices</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          <Table.Row>
            <Table.Cell>Lost/Stolen</Table.Cell>
            <Table.Cell>Lost or Stolen devices</Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Lost or Stolen')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Deployed</Table.Cell>
            <Table.Cell>Device Deployed</Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Deployed')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Awaiting Deployment</Table.Cell>
            <Table.Cell>Devices which are staged for deployment.</Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Awaiting Deployment')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Undergoing Repair</Table.Cell>
            <Table.Cell>
              Devices which are under repair or will be repaired.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Undergoing Repair')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>In-Transit</Table.Cell>
            <Table.Cell>
              Devices which are in transit to merchants, repair facilities, or
              storage facilities.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('In transit')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Storage</Table.Cell>
            <Table.Cell>
              Devices which are offline and have been placed in storage either
              in-store or storage facilities.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Storage')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Retired</Table.Cell>
            <Table.Cell>
              Devices which have been decommissioned and no longer in use.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Retired')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Tampered</Table.Cell>
            <Table.Cell>
              Devices which have been tampered or deemed exploited.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Tampered')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Blocked</Table.Cell>
            <Table.Cell>
              Suspicious activity was found and has been blocked by Payment
              Switch.
            </Table.Cell>
            <Table.Cell className='counter'>
              {statusTotalContent('Blocked')}
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Footer fullWidth>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>
              <b>Total # of Devices:</b>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <b>{totalContent('total', devicesTotal)}</b>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </Fragment>
  );
}
